import React, { createContext, useCallback, useContext, useState } from 'react';

/**
 * Tagged-union of artifacts the side panel knows how to render.
 *
 * Adding a new artifact type means: (1) extend this union with the args the
 * artifact needs to fetch its data, (2) drop a renderer into
 * `ArtifactsPanel.tsx` that switches on `kind`. All open/close plumbing
 * lives here.
 */
export type PanelArtifact =
  | { kind: 'valuation'; projectId: string; valuationId?: string }
  | { kind: 'underwriting'; projectId: string; contextId?: string }
  | { kind: 'knowledge_graph'; projectId: string }
  | { kind: 'report'; title: string; markdown: string; sourceMessageId?: string };

interface ArtifactsPanelContextType {
  artifact: PanelArtifact | null;
  isOpen: boolean;
  /** Open the panel with the given artifact. If the panel is already open it just swaps content. */
  openArtifact: (artifact: PanelArtifact) => void;
  /** Close the panel; the last artifact is forgotten. */
  closeArtifact: () => void;
  /** Reopen the last-shown artifact without re-fetching state (no-op if none). */
  toggle: () => void;
}

const ArtifactsPanelContext = createContext<ArtifactsPanelContextType | undefined>(undefined);

export const ArtifactsPanelProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [artifact, setArtifact] = useState<PanelArtifact | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const openArtifact = useCallback((next: PanelArtifact) => {
    setArtifact(next);
    setIsOpen(true);
  }, []);

  const closeArtifact = useCallback(() => {
    setIsOpen(false);
    // Keep `artifact` around for one frame so the slide-out animation has
    // content to render. Day 5 polish wires a transitionend listener; for
    // now we just clear it on next tick.
    setTimeout(() => setArtifact(null), 250);
  }, []);

  const toggle = useCallback(() => {
    if (!artifact) return;
    setIsOpen((v) => !v);
  }, [artifact]);

  return (
    <ArtifactsPanelContext.Provider value={{ artifact, isOpen, openArtifact, closeArtifact, toggle }}>
      {children}
    </ArtifactsPanelContext.Provider>
  );
};

export const useArtifactsPanel = (): ArtifactsPanelContextType => {
  const ctx = useContext(ArtifactsPanelContext);
  if (!ctx) {
    throw new Error('useArtifactsPanel must be used within an ArtifactsPanelProvider');
  }
  return ctx;
};
